import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ExternalLink, PhoneCall, Sparkles, Dog } from 'lucide-react';

const STEPS = [
  { key: 'call', Icon: PhoneCall },
  { key: 'spa', Icon: Sparkles },
  { key: 'happy', Icon: Dog }
];

function Book() {
  const { t } = useTranslation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Book',
        page_path: window.location.pathname,
      });
    }
  }, []);

  return (
    <div className="book-page" data-testid="book-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-warm-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-4">{t('booking.overline')}</div>
          <h1 className="font-playfair text-5xl md:text-7xl tracking-tighter leading-[1.1] font-medium text-charcoal mb-6" data-testid="book-title">
            {t('booking.title')}
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-charcoal/80">
            {t('booking.subtitle')}
          </p>
        </div>
      </section>

      {/* Booking Widget */}
      <section className="py-16 md:py-24 bg-warm-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl p-6 md:p-10 shadow-[0_16px_48px_rgba(59,48,45,0.08)] border border-accent/30"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="font-playfair text-2xl md:text-3xl font-medium text-charcoal">
                {t('booking.systemTitle')}
              </h2>
              <a
                href="https://booking.moego.pet/ol/CasaPooch/book?utm_medium=embed"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 rounded-full border border-primary text-primary hover:bg-accent/20 transition-colors duration-300 font-medium"
                data-testid="open-booking-new-tab"
              >
                <span>{t('booking.openInNew')}</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="relative w-full" style={{ minHeight: '800px' }}>
              <iframe
                src="https://booking.moego.pet/ol/CasaPooch/book?utm_medium=embed"
                width="100%"
                height="800"
                frameBorder="0"
                title={t('booking.iframeTitle')}
                scrolling="no"
                className="rounded-2xl"
                data-testid="moego-booking-iframe"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-accent/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-playfair text-3xl md:text-4xl font-medium text-charcoal mb-6">
            {t('booking.expectTitle')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {STEPS.map(({ key, Icon }) => (
              <div key={key} data-testid={`book-step-${key}`}>
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-primary" strokeWidth={1.75} />
                </div>
                <h4 className="font-medium text-charcoal mb-2">{t(`booking.steps.${key}.title`)}</h4>
                <p className="text-charcoal/80 text-sm">{t(`booking.steps.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Book;
